import type { Xumm } from "xumm";
import { getXummClient, XamanClient } from "../../../services/external/xaman";
import {
  XrplConnector,
  XrplNetwork,
  XrplTransaction,
  XrplTransactionType,
  XrplTxStatus,
} from "../../types/xrpl";

export class XamanConnector implements XrplConnector {
  xumm: Xumm | null = null;

  private validateXummInitialized(): asserts this is this & { xumm: Xumm } {
    if (!this.xumm) {
      throw new Error("Xumm is not initialized");
    }
  }

  private async initializeXumm(): Promise<void> {
    if (!this.xumm) {
      this.xumm = await getXummClient();
    }
  }

  async connect(): Promise<string> {
    await this.initializeXumm();
    this.validateXummInitialized();

    const connectedAddress = await this.xumm.user.account;

    if (!connectedAddress) {
      throw new Error("User not connected");
    }

    return connectedAddress;
  }

  async signAndSubmit({
    network,
    tx,
  }: {
    tx: XrplTransaction;
    network: XrplNetwork;
  }) {
    this.validateXummInitialized();

    if (!this.xumm.xapp) {
      throw new Error("Not in xApp context");
    }

    if (!this.xumm.payload) {
      throw new Error("Payload not available");
    }

    const xamanNetwork = XamanClient.getXamanNetwork(network);

    const txPayload = await this.xumm.payload.create({
      TransactionType: XrplTransactionType.PAYMENT,
      options: {
        force_network: xamanNetwork,
      },
      txjson: tx,
    });

    await this.xumm.xapp.openSignRequest(txPayload);

    const response: Awaited<ReturnType<XrplConnector["signAndSubmit"]>> =
      await new Promise((resolve, reject) => {
        this.validateXummInitialized();

        if (!this.xumm.xapp) {
          reject(new Error("Not in xApp context"));
        }

        this.xumm.on("payload", async (data) => {
          this.validateXummInitialized();

          if (data && data.reason === "DECLINED") {
            reject(new Error("User rejected the request."));
          }

          if (data && data.reason === "SIGNED") {
            if (!this.xumm.payload) {
              throw new Error("Payload not available");
            }

            const fullPayload = await this.xumm.payload.get(data.uuid);

            if (fullPayload?.response.txid) {
              resolve({
                hash: fullPayload.response.txid,
                status: XrplTxStatus.SUCCESS,
              });
            } else {
              reject(new Error("Transaction hash not found"));
            }
          }
        });
      });

    return response;
  }
}
